import { module as BaseModule } from 'modujs'

let supportsDisplayTransition

function canTransitionDisplay () {
  if (supportsDisplayTransition !== undefined) return supportsDisplayTransition
  if (!window.CSS?.supports?.('transition-behavior', 'allow-discrete') || !document.body) {
    return false
  }
  const probe = document.createElement('div')
  probe.style.cssText = 'transition: display 1s allow-discrete !important; display: block;'
  document.body.appendChild(probe)
  getComputedStyle(probe).display
  probe.style.display = 'none'
  supportsDisplayTransition = getComputedStyle(probe).display === 'block'
  probe.remove()
  return supportsDisplayTransition
}

function supportsTopLayerExit () {
  return window.CSS?.supports?.('overlay', 'auto') && canTransitionDisplay()
}

/**
 * Persistent cart drawer (<dialog>) with smooth open/close.
 * Qty / remove via custom WC AJAX; opens on added_to_cart.
 */
export default class extends BaseModule {
  init () {
    this.dialog = this.el
    if (!(this.dialog instanceof HTMLDialogElement)) return

    this.onToggle = this.onToggle.bind(this)
    this.onClick = this.onClick.bind(this)
    this.onDialogClose = this.onDialogClose.bind(this)
    this.onCancel = this.onCancel.bind(this)
    this.onAddingToCart = this.onAddingToCart.bind(this)
    this.onAddedToCart = this.onAddedToCart.bind(this)
    this.onFragmentRefresh = this.onFragmentRefresh.bind(this)
    this._openAfterAdd = false
    this._closing = false

    document.addEventListener('click', this.onToggle)
    this.dialog.addEventListener('click', this.onClick)
    this.dialog.addEventListener('close', this.onDialogClose)
    this.dialog.addEventListener('cancel', this.onCancel)

    this.bindJqueryEvents()
    this.bindClosedByFallback()
  }

  destroy () {
    document.removeEventListener('click', this.onToggle)
    this.dialog.removeEventListener('click', this.onClick)
    this.dialog.removeEventListener('close', this.onDialogClose)
    this.dialog.removeEventListener('cancel', this.onCancel)
    if (this._jqueryBound && window.jQuery) {
      const $body = window.jQuery(document.body)
      $body.off('adding_to_cart', this.onAddingToCart)
      $body.off('added_to_cart', this.onAddedToCart)
      $body.off('wc_fragments_refreshed wc_fragments_loaded', this.onFragmentRefresh)
      this._jqueryBound = false
    }
    if (this._jqueryTimer) {
      clearTimeout(this._jqueryTimer)
      this._jqueryTimer = null
    }
  }

  bindJqueryEvents () {
    if (window.jQuery) {
      const $body = window.jQuery(document.body)
      $body.on('adding_to_cart', this.onAddingToCart)
      $body.on('added_to_cart', this.onAddedToCart)
      $body.on('wc_fragments_refreshed wc_fragments_loaded', this.onFragmentRefresh)
      this._jqueryBound = true
      return
    }

    this._jqueryTimer = setTimeout(() => this.bindJqueryEvents(), 50)
  }

  onDialogClose () {
    this._closing = false
    this.dialog.removeAttribute('data-closing')
    if (window.lenis) window.lenis.start()
  }

  onCancel (event) {
    // Route Esc / closedby through animated close instead of an instant dismiss.
    event.preventDefault()
    this.close()
  }

  onAddingToCart () {
    this._openAfterAdd = true
  }

  open () {
    if (this.dialog.open && !this.dialog.hasAttribute('data-closing')) return
    this.dialog.removeAttribute('data-closing')
    this._closing = false
    if (!this.dialog.open) {
      this.dialog.showModal()
    }
    if (window.lenis) window.lenis.stop()
  }

  async close () {
    if (!this.dialog.open || this._closing) return
    this._closing = true

    if (!supportsTopLayerExit()) {
      this.dialog.setAttribute('data-closing', '')
      const animations = this.dialog.getAnimations({ subtree: true })
      if (animations.length > 0) {
        await Promise.race([
          Promise.allSettled(animations.map((a) => a.finished)),
          new Promise((r) => setTimeout(r, 2000))
        ])
      }
      if (!this.dialog.hasAttribute('data-closing')) return
      this.dialog.removeAttribute('data-closing')
    }

    if (this.dialog.open) {
      this.dialog.close()
    }
    if (window.lenis) window.lenis.start()
    this._closing = false
  }

  onToggle (event) {
    const toggle = event.target.closest('.js-cart-toggle')
    if (!toggle) return

    // Always drive open ourselves so Lenis + animation stay in sync.
    event.preventDefault()
    if (this.dialog.open && !this.dialog.hasAttribute('data-closing')) {
      this.close()
      return
    }
    this.open()
  }

  onClick (event) {
    // Animated close for method="dialog" (Close button).
    const dialogForm = event.target.closest('form[method="dialog"]')
    if (dialogForm && this.dialog.contains(dialogForm)) {
      event.preventDefault()
      this.close()
      return
    }

    const qtyBtn = event.target.closest('.js-cart-qty')
    if (qtyBtn) {
      event.preventDefault()
      this.updateQty(qtyBtn)
      return
    }

    const remove = event.target.closest('.js-cart-remove')
    if (remove) {
      event.preventDefault()
      this.removeItem(remove.dataset.key)
      return
    }
  }

  onAddedToCart () {
    this._openAfterAdd = true
    this.open()
  }

  onFragmentRefresh () {
    if (this._openAfterAdd) {
      this._openAfterAdd = false
      requestAnimationFrame(() => this.open())
      return
    }

    if (!this.dialog.open && window.lenis) window.lenis.start()
  }

  bindClosedByFallback () {
    // Light-dismiss when closedby is unsupported, with animated close.
    if ('closedBy' in HTMLDialogElement.prototype) return

    let pressedOutside = false
    const outside = (e) => {
      const r = this.dialog.getBoundingClientRect()
      return e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom
    }

    this.dialog.addEventListener('pointerdown', (e) => {
      pressedOutside = outside(e)
    })
    this.dialog.addEventListener('pointerup', (e) => {
      if (pressedOutside && outside(e)) this.close()
    })
  }

  getAjaxUrl () {
    return (window.wc_add_to_cart_params && window.wc_add_to_cart_params.wc_ajax_url)
      ? window.wc_add_to_cart_params.wc_ajax_url.toString().replace('%%endpoint%%', 'julietmerie_update_cart_item')
      : '/?wc-ajax=julietmerie_update_cart_item'
  }

  async updateQty (button) {
    const key = button.dataset.key
    const row = button.closest('[data-cart-item-key]')
    const valueEl = row && row.querySelector('.js-cart-qty-value')
    let qty = valueEl ? parseInt(valueEl.textContent, 10) : 1
    if (Number.isNaN(qty)) qty = 1

    if (button.dataset.action === 'minus') qty -= 1
    if (button.dataset.action === 'plus') qty += 1
    if (qty < 0) qty = 0

    await this.postCartUpdate(key, qty)
  }

  async removeItem (key) {
    if (!key) return
    await this.postCartUpdate(key, 0)
  }

  async postCartUpdate (key, quantity) {
    const body = new FormData()
    body.append('key', key)
    body.append('quantity', String(quantity))

    try {
      const response = await fetch(this.getAjaxUrl(), {
        method: 'POST',
        credentials: 'same-origin',
        body
      })
      const data = await response.json()
      if (!data || !data.success || !data.data || !data.data.fragments) return
      this.applyFragments(data.data.fragments)
    } catch (err) {
      console.error(err)
    }
  }

  applyFragments (fragments) {
    Object.keys(fragments).forEach((selector) => {
      document.querySelectorAll(selector).forEach((node) => {
        const temp = document.createElement('div')
        temp.innerHTML = fragments[selector].trim()
        const next = temp.firstElementChild
        if (next) node.replaceWith(next)
      })
    })

    if (window.jQuery) {
      window.jQuery(document.body).trigger('wc_fragments_refreshed')
    }
  }
}
